import type { FirstTouchContext } from './types';

export const UTM_FIELDS = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmTerm', 'utm_term'],
  ['utmContent', 'utm_content'],
] as const;

function orUndefined(value: string | null): string | undefined {
  return value !== null && value !== '' ? value : undefined;
}

export function readFirstTouchContext(): FirstTouchContext {
  const params = new URLSearchParams(location.search);

  const referrerRaw = document.referrer;
  const referrer =
    referrerRaw && !referrerRaw.startsWith(location.origin)
      ? referrerRaw
      : undefined;

  const context: FirstTouchContext = { referrer };
  for (const [contextKey, paramName] of UTM_FIELDS) {
    context[contextKey] = orUndefined(params.get(paramName));
  }
  return context;
}

/**
 * Returns `href` with UTM params from `context` appended, preserving any
 * params already on the URL. Returns `href` unchanged if:
 *   - the URL is not http(s)
 *   - context has no UTM values
 *   - all UTM keys are already present on the URL
 */
export function decorateUrl(href: string, context: FirstTouchContext): string {
  let url: URL;
  try {
    url = new URL(href, location.href);
  } catch {
    return href;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return href;
  }

  let modified = false;
  for (const [contextKey, paramName] of UTM_FIELDS) {
    const value = context[contextKey];
    if (value && !url.searchParams.has(paramName)) {
      url.searchParams.set(paramName, value);
      modified = true;
    }
  }

  // Preserve the original string when nothing changed (idempotency, no
  // normalization). Only stringify after applying params (may resolve relative
  // paths to absolute).
  return modified ? url.toString() : href;
}
